import { useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import Logo from '../brand/Logo';
import FocusModeTabs from '../Focus/FocusModeTabs';
import { isFocusPath } from '../Focus/focusModes';
import './MobileHeader.css';

/**
 * Route-to-title mapping for the mobile header.
 *
 * The Focus mode paths are deliberately ABSENT: `getPageTitle` asks the registry
 * instead. Listing `/focus`, `/circuits` and `/rituals` here made this the one
 * place focusModes.js did not reach, so a fourth mode would have silently
 * inherited the 'Alphafocus' fallback. `/flows` stays — it is a redirect alias,
 * not a mode, and is not in the registry.
 */
const PAGE_TITLES = {
    '/': 'Home',
    '/today': 'Today',
    '/flows': 'Focus',
    '/tasks': 'Tasks',
    '/projects': 'Projects',
    '/quick-capture': 'Quick Capture',
    '/dashboard': 'Dashboard',
    '/identities': 'Identities',
    '/areas': 'Areas',
    '/resources': 'Resources',
    '/archives': 'Archives',
    '/sessions': 'Sessions',
    '/gamification': 'Gamification',
    '/weekly-review': 'Weekly Review',
    '/reviews': 'Reviews',
    '/settings': 'Settings',
};

function getPageTitle(pathname) {
    // Every Focus mode is one destination — ask the registry, never a hardcoded list.
    if (isFocusPath(pathname)) return 'Focus';
    // Exact match first
    if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
    // Project detail
    if (pathname.startsWith('/projects/')) return 'Project';
    // Fallback
    return 'Alphafocus';
}

/**
 * MobileHeader — Figma "Nav / Top Bar".
 *
 * @param {'mark'|'lockup'|'none'} brand  the Figma Brand axis. 'mark' (default)
 *   shows the glyph beside the page title; 'lockup' shows mark + wordmark and
 *   stands in for the title, because repeating "Alphafocus" next to it would be
 *   the same name twice; 'none' drops brand entirely.
 *
 * The Logo carries its own sizing and light/dark asset switching (see Logo.css),
 * so no header CSS is involved — `mobile-header__brand` exists purely as an
 * override hook.
 *
 * THE TITLE STANDS DOWN FOR THE TAB BAR. This bar is `space-between` with no
 * overflow handling, and `.focus-mode-tabs` is `flex-shrink: 0` + `nowrap` — so
 * the tabs never clip and the title absorbs the whole shortfall instead. Measured
 * in the running app with all three modes enabled: 9.1px of title at 375px, 0px
 * at 320px. (Only two tabs render with the `rituals` module off, which is why it
 * went unnoticed.) Truncating to an ellipsis would only formalise the 9px; the
 * honest fix is to drop a word the tab bar already says, since the tabs name the
 * mode and the brand mark still identifies the app.
 */
export default function MobileHeader({ onHamburgerPress, hidden, actions, brand = 'mark' }) {
    const location = useLocation();
    const title = getPageTitle(location.pathname);
    const isFocusPage = isFocusPath(location.pathname);
    const showBrand = brand === 'mark' || brand === 'lockup';
    const showTitle = brand !== 'lockup' && !isFocusPage;

    // `inert` while hidden, the same fix Sidebar.jsx makes for its own
    // zen-hidden state: `.mobile-header--hidden` only fades/translates the
    // bar and kills pointer-events, none of which takes a node out of the
    // tab order or the accessibility tree. Without this, the hamburger and
    // the Inbox/AI Coach actions stayed keyboard- and screen-reader-
    // reachable while completely invisible during a running focus session.
    // `|| undefined` keeps the enabled case an ABSENT attribute —
    // `inert="false"` is still inert.
    return (
        <header
            className={`mobile-header ${hidden ? 'mobile-header--hidden' : ''}`}
            role="banner"
            inert={hidden || undefined}
        >
            <div className="mobile-header__left">
                <button
                    className="btn btn-ghost btn-lg btn-icon mobile-header__hamburger"
                    onClick={onHamburgerPress}
                    aria-label="Open navigation menu"
                >
                    <Menu size={20} />
                </button>
                {showBrand && (
                    <Logo
                        variant={brand}
                        size="md"
                        className="mobile-header__brand"
                    />
                )}
                {showTitle && <h1 className="mobile-header__title">{title}</h1>}
            </div>
            {isFocusPage && <FocusModeTabs size="sm" />}
            {actions && (
                <div className="mobile-header__actions">
                    {actions}
                </div>
            )}
        </header>
    );
}

